import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useHealthAndSafetyService } from '../../../../apis/healthAndSafety/useHealthAndSafetyService';
import {
  THealthIncident,
  THealthIncidentListApiResponse,
  THealthIncidentParams,
} from '../../../../apis/healthAndSafety/healthAndSafety.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getHealthIncidentListKey = 'get-health-incident-list';
export const getHealthIncidentByIdKey = 'get-health-incident-by-id';

// ===================== LIST HOOK =====================
export const useHealthIncidentListHook = (
  params: THealthIncidentParams,
  options?: UseQueryOptions<THealthIncidentListApiResponse, TQueryError>
): UseQueryResult<THealthIncidentListApiResponse, TQueryError> => {
  const { getHealthIncidentList } = useHealthAndSafetyService();

  return useQuery<THealthIncidentListApiResponse, TQueryError>({
    queryKey: [getHealthIncidentListKey, params],
    queryFn: () => getHealthIncidentList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useHealthIncidentByIdHook = (
  id: string,
  options?: UseQueryOptions<THealthIncident, TQueryError>
): UseQueryResult<THealthIncident, TQueryError> => {
  const { getHealthIncidentById } = useHealthAndSafetyService();

  return useQuery<THealthIncident, TQueryError>({
    queryKey: [getHealthIncidentByIdKey, id],
    queryFn: () => getHealthIncidentById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateHealthIncidentHook = (
  options?: UseMutationOptions<
    THealthIncident,
    TMutationError,
    Omit<THealthIncident, 'id' | 'createdAt' | 'updatedAt'>
  >
): UseMutationResult<
  THealthIncident,
  TMutationError,
  Omit<THealthIncident, 'id' | 'createdAt' | 'updatedAt'>
> => {
  const { createHealthIncident } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    THealthIncident,
    TMutationError,
    Omit<THealthIncident, 'id' | 'createdAt' | 'updatedAt'>
  >({
    mutationFn: (incident) => createHealthIncident(incident),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getHealthIncidentListKey] });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateHealthIncidentHook = (
  options?: UseMutationOptions<
    THealthIncident,
    TMutationError,
    { id: string; data: Partial<THealthIncident> }
  >
): UseMutationResult<
  THealthIncident,
  TMutationError,
  { id: string; data: Partial<THealthIncident> }
> => {
  const { updateHealthIncident } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    THealthIncident,
    TMutationError,
    { id: string; data: Partial<THealthIncident> }
  >({
    mutationFn: ({ id, data }) => updateHealthIncident(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getHealthIncidentListKey] });
      queryClient.invalidateQueries({
        queryKey: [getHealthIncidentByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteHealthIncidentHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteHealthIncident } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteHealthIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getHealthIncidentListKey] });
    },
    ...options,
  });
};
